import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getPageContent, subscribeToPage } from '../firebase/firebase-config';

function MandatoryDisclosure() {
  const [content, setContent] = useState({ 
    generalInfo: [], 
    documents: [],
    resultDocuments: [],
    resultClassX: [],
    resultClassXII: [],
    staff: [],
    infrastructure: [],
    title: 'CBSE MANDATORY DISCLOSURE',
    sliderImages: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await getPageContent('mandatoryDisclosure');
        setContent(prev => ({ ...prev, ...data }));
      } catch (error) { 
        console.error(error); 
      } finally { 
        setLoading(false); 
      }
    };
    loadData();
    const unsubscribe = subscribeToPage('mandatoryDisclosure', (data) => { 
      if (data) setContent(prev => ({ ...prev, ...data })); 
    });
    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="disclosure-page">
        <div className="disclosure-container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading CBSE Mandatory Disclosure...</p>
          </div>
        </div>
      </div>
    );
  }

  const generalInfo = (content.generalInfo || []).filter(item => item.label?.trim() && item.value?.trim());
  const documents = (content.documents || []).filter(doc => doc.label?.trim());
  const resultDocuments = (content.resultDocuments || []).filter(doc => doc.label?.trim());
  const resultClassX = (content.resultClassX || []).filter(row => row.year?.trim());
  const resultClassXII = (content.resultClassXII || []).filter(row => row.year?.trim());
  const staff = (content.staff || []).filter(item => item.information?.trim());
  const infrastructure = (content.infrastructure || []).filter(item => item.information?.trim());

  const hasAnyContent = generalInfo.length > 0 || documents.length > 0 || resultDocuments.length > 0 || 
                        resultClassX.length > 0 || resultClassXII.length > 0 || staff.length > 0 || infrastructure.length > 0;

  if (!hasAnyContent) {
    return (
      <div className="disclosure-page">
        <div className="disclosure-container">
          <h1 className="disclosure-main-title">{content.title || 'CBSE MANDATORY DISCLOSURE'}</h1>
          <div className="empty-state">
            <div className="empty-icon">⚖️</div>
            <h3>No Information Available</h3>
            <p>CBSE mandatory disclosure information will be displayed here once added by the school administration.</p>
          </div>
        </div>
        <style>{cssStyles}</style>
      </div>
    );
  }

  return (
    <div className="disclosure-page">
      <div className="disclosure-container">
        <h1 className="disclosure-main-title">{content.title || 'CBSE MANDATORY DISCLOSURE'}</h1>
        
        {/* SECTION A - GENERAL INFORMATION */}
        {generalInfo.length > 0 && (
          <div className="disclosure-section">
            <h2 className="disclosure-section-title">A: GENERAL INFORMATION</h2>
            <div className="table-wrapper">
              <table className="cbse-table">
                <thead>
                  <tr>
                    <th>SL NO.</th>
                    <th>INFORMATION</th>
                    <th>DETAILS</th>
                  </tr>
                </thead>
                <tbody>
                  {generalInfo.map((item, idx) => (
                    <tr key={item.id}>
                      <td data-label="SL NO." className="text-center">{idx + 1}</td>
                      <td data-label="INFORMATION" className="info-label-cell">
                        <strong>{item.label}</strong>
                      </td>
                      <td data-label="DETAILS">{item.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* SECTION B - DOCUMENTS AND INFORMATION */}
        {documents.length > 0 && (
          <div className="disclosure-section">
            <h2 className="disclosure-section-title">B: DOCUMENTS AND INFORMATION</h2>
            <div className="table-wrapper">
              <table className="cbse-table">
                <thead>
                  <tr>
                    <th>SL NO.</th>
                    <th>DOCUMENT / INFORMATION</th>
                    <th>UPLOAD DOCUMENTS</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc, idx) => (
                    <tr key={doc.id}>
                      <td data-label="SL NO." className="text-center">{idx + 1}</td>
                      <td data-label="DOCUMENT / INFORMATION">
                        <strong>{doc.label}</strong>
                      </td>
                      <td data-label="UPLOAD DOCUMENTS" className="text-center">
                        {doc.file?.url ? (
                          <a href={doc.file.url} target="_blank" rel="noopener noreferrer" className="document-btn">📄 VIEW DOCUMENT</a>
                        ) : (
                          <span className="empty-cell">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* SECTION C1 - RESULT AND ACADEMICS DOCUMENTS */}
        {resultDocuments.length > 0 && (
          <div className="disclosure-section">
            <h2 className="disclosure-section-title">C1: RESULT AND ACADEMICS DOCUMENTS</h2>
            <div className="table-wrapper">
              <table className="cbse-table">
                <thead>
                  <tr>
                    <th>SL NO.</th>
                    <th>DOCUMENTS / INFORMATION</th>
                    <th>UPLOAD DOCUMENTS</th>
                  </tr>
                </thead>
                <tbody>
                  {resultDocuments.map((doc, idx) => (
                    <tr key={doc.id}>
                      <td data-label="SL NO." className="text-center">{idx + 1}</td>
                      <td data-label="DOCUMENTS / INFORMATION">
                        <strong>{doc.label}</strong>
                      </td>
                      <td data-label="UPLOAD DOCUMENTS" className="text-center">
                        {doc.file?.url ? (
                          <a href={doc.file.url} target="_blank" rel="noopener noreferrer" className="document-btn">📄 VIEW DOCUMENT</a>
                        ) : (
                          <span className="empty-cell">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* SECTION C2 - RESULT CLASS X */}
        {resultClassX.length > 0 && (
          <div className="disclosure-section">
            <h2 className="disclosure-section-title">C2: RESULT CLASS X</h2>
            <div className="table-wrapper">
              <table className="cbse-table">
                <thead>
                  <tr>
                    <th>S.NO</th>
                    <th>YEAR</th>
                    <th>NO. OF REGISTERED STUDENTS</th>
                    <th>NO. OF STUDENTS PASSED</th>
                    <th>PASS PERCENTAGE</th>
                    <th>REMARKS</th>
                  </tr>
                </thead>
                <tbody>
                  {resultClassX.map((row, idx) => (
                    <tr key={row.id}>
                      <td data-label="S.NO" className="text-center">{row.sNo || idx + 1}</td>
                      <td data-label="YEAR" className="text-center"><strong>{row.year}</strong></td>
                      <td data-label="NO. OF REGISTERED STUDENTS" className="text-center">{row.registered}</td>
                      <td data-label="NO. OF STUDENTS PASSED" className="text-center">{row.passed}</td>
                      <td data-label="PASS PERCENTAGE" className="text-center"><strong>{row.percentage}%</strong></td>
                      <td data-label="REMARKS">{row.remarks}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* SECTION C3 - RESULT CLASS XII */}
        {resultClassXII.length > 0 && (
          <div className="disclosure-section">
            <h2 className="disclosure-section-title">C3: RESULT CLASS XII</h2>
            <div className="table-wrapper">
              <table className="cbse-table">
                <thead>
                  <tr>
                    <th>S.NO</th>
                    <th>YEAR</th>
                    <th>NO. OF REGISTERED STUDENTS</th>
                    <th>NO. OF STUDENTS PASSED</th>
                    <th>PASS PERCENTAGE</th>
                    <th>REMARKS</th>
                  </tr>
                </thead>
                <tbody>
                  {resultClassXII.map((row, idx) => (
                    <tr key={row.id}>
                      <td data-label="S.NO" className="text-center">{row.sNo || idx + 1}</td>
                      <td data-label="YEAR" className="text-center"><strong>{row.year}</strong></td>
                      <td data-label="NO. OF REGISTERED STUDENTS" className="text-center">{row.registered}</td>
                      <td data-label="NO. OF STUDENTS PASSED" className="text-center">{row.passed}</td>
                      <td data-label="PASS PERCENTAGE" className="text-center"><strong>{row.percentage}%</strong></td>
                      <td data-label="REMARKS">{row.remarks}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* SECTION D - STAFF (TEACHING) */}
        {staff.length > 0 && (
          <div className="disclosure-section">
            <h2 className="disclosure-section-title">D: STAFF (TEACHING)</h2>
            <div className="table-wrapper">
              <table className="cbse-table">
                <thead>
                  <tr>
                    <th>S.NO</th>
                    <th>INFORMATION</th>
                    <th>NUMBER / STRENGTH</th>
                    <th>NAME AND QUALIFICATIONS</th>
                    <th>UPLOAD DOCUMENTS</th>
                  </tr>
                </thead>
                <tbody>
                  {staff.map((item, idx) => (
                    <tr key={item.id}>
                      <td data-label="S.NO" className="text-center">{item.sNo || idx + 1}</td>
                      <td data-label="INFORMATION">
                        <strong>{item.information}</strong>
                      </td>
                      <td data-label="NUMBER / STRENGTH" className="text-center">
                        <strong>{item.number}</strong>
                      </td>
                      <td data-label="NAME AND QUALIFICATIONS" className="qualifications-cell">{item.qualifications}</td>
                      <td data-label="UPLOAD DOCUMENTS" className="text-center">
                        {item.file?.url ? (
                          <a href={item.file.url} target="_blank" rel="noopener noreferrer" className="document-btn">📄 VIEW</a>
                        ) : (
                          <span className="empty-cell">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* SECTION E - SCHOOL INFRASTRUCTURE */}
        {infrastructure.length > 0 && (
          <div className="disclosure-section">
            <h2 className="disclosure-section-title">E: SCHOOL INFRASTRUCTURE</h2>
            <div className="table-wrapper">
              <table className="cbse-table">
                <thead>
                  <tr>
                    <th>S.NO</th>
                    <th>INFORMATION</th>
                    <th>DETAILS</th>
                    <th>DOCUMENT / MEDIA</th>
                  </tr>
                </thead>
                <tbody>
                  {infrastructure.map((item, idx) => (
                    <tr key={item.id}>
                      <td data-label="S.NO" className="text-center">{item.sNo || idx + 1}</td>
                      <td data-label="INFORMATION">
                        <strong>{item.information}</strong>
                      </td>
                      <td data-label="DETAILS" className="details-cell">{item.details}</td>
                      <td data-label="DOCUMENT / MEDIA" className="media-cell">
                        {item.file?.url && (
                          <a href={item.file.url} target="_blank" rel="noopener noreferrer" className="document-btn">📄 DOCUMENT</a>
                        )}
                        {item.youtubeLink && (
                          <a href={item.youtubeLink} target="_blank" rel="noopener noreferrer" className="video-btn">▶ WATCH VIDEO</a>
                        )}
                        {!item.file?.url && !item.youtubeLink && (
                          <span className="empty-cell">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="back-link">
          <Link to="/academics" className="back-btn">← BACK TO ACADEMICS</Link>
        </div>
      </div>

      <style>{cssStyles}</style>
    </div>
  );
}

const cssStyles = `
  .disclosure-page {
    background: #ffffff;
    min-height: 100vh;
    padding: 40px 0;
    font-family: 'Arial', 'Helvetica', sans-serif;
  }
  
  .disclosure-container {
    width: 95%;
    max-width: 1400px;
    margin: 0 auto;
  }
  
  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 60vh;
  }
  
  .loading-spinner {
    width: 50px;
    height: 50px;
    border: 4px solid #e2e8f0;
    border-top-color: #1a3a6b;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  
  /* ===== PAGE TITLE ===== */
  .disclosure-main-title {
    text-align: center;
    font-size: 42px;
    font-weight: 800;
    color: #000000;
    margin-bottom: 40px;
    text-transform: uppercase;
    letter-spacing: 2px;
    border-bottom: 4px solid #1a3a6b;
    padding-bottom: 20px;
    display: inline-block;
    width: 100%;
  }
  
  /* ===== SECTION TITLE ===== */
  .disclosure-section {
    margin-bottom: 50px;
  }
  
  .disclosure-section-title {
    font-size: 24px;
    font-weight: 800;
    color: #1a3a6b;
    text-transform: uppercase;
    margin-bottom: 20px;
    padding-left: 10px;
    border-left: 5px solid #f59e0b;
    letter-spacing: 0.5px;
  }
  
  /* ===== TABLE WRAPPER ===== */
  .table-wrapper {
    width: 100%;
    overflow-x: auto;
  }
  
  /* ===== CBSE OFFICIAL TABLE ===== */
  .cbse-table {
    width: 100%;
    border-collapse: collapse;
    background: #ffffff;
    font-family: 'Arial', 'Helvetica', sans-serif;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  }
  
  /* ===== TABLE HEADER ===== */
  .cbse-table thead th {
    background: #1a3a6b;
    color: #ffffff;
    font-size: 15px;
    font-weight: 800;
    text-transform: uppercase;
    padding: 14px 12px;
    border: 1px solid #2d4a8a;
    text-align: center;
    vertical-align: middle;
    letter-spacing: 0.5px;
  }
  
  /* ===== TABLE BODY CELLS ===== */
  .cbse-table tbody td {
    border: 1px solid #d1d5db;
    padding: 12px 12px;
    font-size: 14px;
    color: #1f2937;
    vertical-align: top;
    line-height: 1.5;
    background: #ffffff;
  }
  
  /* ===== INFO LABEL CELL (BOLD) ===== */
  .info-label-cell strong,
  .cbse-table tbody td:first-child strong,
  .cbse-table tbody td:nth-child(2) strong {
    font-weight: 700;
    color: #000000;
  }
  
  /* ===== SERIAL NUMBER COLUMN ===== */
  .cbse-table tbody td:first-child {
    text-align: center;
    font-weight: 700;
    width: 60px;
    background-color: #f8fafc;
  }
  
  /* ===== TEXT CENTER ===== */
  .text-center {
    text-align: center;
  }
  
  /* ===== QUALIFICATIONS CELL ===== */
  .qualifications-cell {
    white-space: pre-wrap;
    word-break: break-word;
    font-size: 13px;
  }
  
  /* ===== DETAILS CELL ===== */
  .details-cell {
    white-space: pre-wrap;
    word-break: break-word;
  }
  
  /* ===== MEDIA CELL ===== */
  .media-cell {
    text-align: center;
    vertical-align: middle;
  }
  
  /* ===== DOCUMENT BUTTON ===== */
  .document-btn {
    display: inline-block;
    padding: 6px 12px;
    background: #1a3a6b;
    color: #ffffff;
    text-decoration: none;
    font-size: 12px;
    font-weight: 700;
    border-radius: 4px;
    transition: all 0.3s ease;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  .document-btn:hover {
    background: #f59e0b;
    color: #000000;
    transform: translateY(-1px);
  }
  
  /* ===== VIDEO BUTTON ===== */
  .video-btn {
    display: inline-block;
    padding: 6px 12px;
    background: #dc2626;
    color: #ffffff;
    text-decoration: none;
    font-size: 12px;
    font-weight: 700;
    border-radius: 4px;
    transition: all 0.3s ease;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  .video-btn:hover {
    background: #b91c1c;
    transform: translateY(-1px);
  }
  
  /* ===== EMPTY CELL ===== */
  .empty-cell {
    color: #9ca3af;
    font-style: italic;
  }
  
  /* ===== BACK LINK ===== */
  .back-link {
    text-align: center;
    margin-top: 40px;
    padding-top: 20px;
    border-top: 2px solid #e5e7eb;
  }
  
  .back-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: transparent;
    color: #1a3a6b;
    padding: 10px 24px;
    border: 2px solid #1a3a6b;
    border-radius: 8px;
    text-decoration: none;
    font-weight: 700;
    font-size: 14px;
    text-transform: uppercase;
    transition: all 0.3s ease;
    letter-spacing: 0.5px;
  }
  
  .back-btn:hover {
    background: #1a3a6b;
    color: #ffffff;
    transform: translateX(-5px);
  }
  
  /* ===== EMPTY STATE ===== */
  .empty-state {
    text-align: center;
    padding: 80px 20px;
    background: #f8fafc;
    border: 2px dashed #cbd5e1;
    border-radius: 16px;
    margin-top: 40px;
  }
  
  .empty-icon {
    font-size: 64px;
    margin-bottom: 20px;
  }
  
  .empty-state h3 {
    color: #1f2937;
    margin-bottom: 10px;
    font-size: 24px;
  }
  
  .empty-state p {
    color: #6b7280;
    font-size: 16px;
  }
  
  /* ===== RESPONSIVE STYLES ===== */
  @media (min-width: 1024px) {
    .cbse-table thead th {
      font-size: 15px;
      padding: 14px 12px;
    }
    .cbse-table tbody td {
      font-size: 14px;
      padding: 12px 12px;
    }
  }
  
  @media (min-width: 768px) and (max-width: 1023px) {
    .cbse-table thead th {
      font-size: 13px;
      padding: 10px 8px;
    }
    .cbse-table tbody td {
      font-size: 13px;
      padding: 10px 8px;
    }
    .document-btn, .video-btn {
      padding: 5px 10px;
      font-size: 11px;
    }
  }
  
  @media (max-width: 768px) {
    .disclosure-page {
      padding: 20px 0;
    }
    .disclosure-container {
      width: 96%;
    }
    .disclosure-main-title {
      font-size: 24px;
      margin-bottom: 25px;
      padding-bottom: 12px;
    }
    .disclosure-section-title {
      font-size: 18px;
    }
    .cbse-table thead th {
      font-size: 11px;
      padding: 8px 6px;
    }
    .cbse-table tbody td {
      font-size: 11px;
      padding: 8px 6px;
    }
    .document-btn, .video-btn {
      padding: 4px 8px;
      font-size: 10px;
    }
    .back-btn {
      font-size: 12px;
      padding: 8px 16px;
    }
  }
  
  @media (max-width: 480px) {
    .disclosure-main-title {
      font-size: 20px;
    }
    .disclosure-section-title {
      font-size: 16px;
    }
    .cbse-table thead th {
      font-size: 10px;
      padding: 6px 4px;
    }
    .cbse-table tbody td {
      font-size: 10px;
      padding: 6px 4px;
    }
    .document-btn, .video-btn {
      font-size: 9px;
      padding: 3px 6px;
    }
  }
`;

export default MandatoryDisclosure;